import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { doc, p } from '@atlaskit/adf-utils/builders';
import type { DocNode } from '@atlaskit/adf-schema';

import { AIAgentResponseState, useAIAgentStreaming } from '@atlassian/ai-agent-streaming';
import { adfToMarkdownPlus, markdownPlusToAdf } from '@atlassian/adf-markdown-transformer';
import type { IDMap } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import type { AiIssueCreateState } from '@atlassian/ai-issue-create';

import { usePageContentId } from '@confluence/page-context';

import type { Issue, JiraAIRequest, JiraAIResponse } from '../__types__/AiAgentStreamingTypes';
import { AiStates, type AiBulkIssuesListType } from '../providers/BulkCreateContextProvider';

import type {
	BulkIssueADFQuery as BulkIssueADFQueryType,
	BulkIssueADFQueryVariables,
} from './ADFtoMarkdownResources/__types__/BulkIssueADFQuery';
import { BulkIssueADFQuery } from './ADFtoMarkdownResources/BulkIssueADFQuery.graphql';

export const useAIAgentStreamingData = () => {
	const [completedIssues, setCompletedIssues] = useState<AiBulkIssuesListType>([]);
	const [markdownPlusContent, setMarkdownPlusContent] = useState('');
	const [pageIdMap, setPageIdMap] = useState<IDMap | undefined>(undefined);
	const [currentAiState, setCurrentAiState] = useState<AiIssueCreateState>(AiStates.LOADING);

	const [contentId] = usePageContentId();

	const pageId = contentId ?? '';

	// Used to keep track of the issues that have been added to the list and give a unique id
	const issueIndex = useRef(0);

	// Needed to convert confluence content into MD+ format for the ai request
	useQuery<BulkIssueADFQueryType, BulkIssueADFQueryVariables>(BulkIssueADFQuery, {
		variables: {
			contentId: pageId,
		},
		onError: (_adfError) => {
			setCurrentAiState(AiStates.ERROR);
			// TODO: Remove this throw as the error is not caught anywhere
			// throw new Error('Error calling query', adfError);
		},
		fetchPolicy: 'network-only',
		onCompleted: (adfData) => {
			const adf = adfData?.singleContent?.body?.atlas_doc_format?.value;
			if (adf) {
				try {
					const parsedAdf: DocNode = adf ? JSON.parse(adf) : doc(p(''));
					const { markdown: markdownPlus, idMap } = adfToMarkdownPlus(parsedAdf);
					setMarkdownPlusContent(markdownPlus);
					setPageIdMap(idMap);
				} catch (err) {
					setCurrentAiState(AiStates.ERROR);
					// TODO: Remove this throw as the error is not caught anywhere
					// throw new Error('Failed to parse adf');
				}
			}
		},
		skip: currentAiState !== AiStates.LOADING,
	});

	const request: JiraAIRequest = {
		recipient_agent_named_id: 'ai_issue_create_agent',
		agent_input_context: {
			application: 'Confluence',
			context: {
				confluence_page_markdown_plus: markdownPlusContent,
				highlighted_text: null,
			},
			suggested_issues_config: {
				max_issues: 10,
				suggested_issue_field_types: [
					{
						issue_type: 'Task',
						fields: [
							{
								field_name: 'Summary',
								field_type: 'Short-Text',
							},
							{
								field_name: 'Description',
								field_type: 'Paragraph',
							},
						],
					},
				],
			},
		},
	};

	const skipDataStremaing = !markdownPlusContent;
	const [{ content, responseState }] = useAIAgentStreaming<JiraAIRequest, JiraAIResponse>(
		request,
		{
			enablePartialJson: true,
			headers: {
				'x-experience-id': 'ai-issue-create-confluence-bulk',
				'x-product': 'confluence',
			},
		},
		skipDataStremaing,
	);

	useEffect(() => {
		const getIssueDetails = () => {
			return {
				iconUrl: '',
				name: '',
				id: '',
			};
		};

		// Temp add issues until we can handle partial responses
		const addIssues = (issues: Issue[]) => {
			const newIssues = issues.map((issue) => ({
				id: String(issueIndex.current++),
				summary: issue.field_values.Summary,
				description: markdownPlusToAdf({
					markdown: issue.field_values.Description,
					idMap: pageIdMap,
				}),
				issueType: getIssueDetails(),
			}));
			setCompletedIssues(newIssues);
		};

		// Switch statement to handle the different response states
		switch (responseState) {
			case AIAgentResponseState.Initialization:
				// Not needed really just want to track all the states
				setCompletedIssues([]);
				break;
			case AIAgentResponseState.AnswerPart:
				// Will update at a later time to handle partial responses
				break;
			case AIAgentResponseState.FinalResponse:
				const issues = content.suggested_issues;
				try {
					// Setting list when all issues are returned for now, will update to handle partial responses
					addIssues(issues);
					setCurrentAiState(AiStates.IDLE);
				} catch (error) {
					setCurrentAiState(AiStates.ERROR);
					// TODO: Remove this throw as the error is not caught anywhere
					// throw new Error('Failed to set completed issues', error);
				}
				break;
			case AIAgentResponseState.Error: {
				setCurrentAiState(AiStates.ERROR);
				// TODO: Remove this throw as the error is not caught anywhere
				// throw new Error('Failed to set completed issues');
			}
			default:
				break;
		}
	}, [content, pageIdMap, responseState, issueIndex]);

	return {
		content: completedIssues,
		currentAiState,
		responseState,
	};
};
